import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ref, onValue } from 'firebase/database';
import { rtdb } from '../firebase';
import api from '../services/api';

export default function AdminChat() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [chatList, setChatList] = useState([]);
  const [selectedId, setSelectedId] = useState(searchParams.get('user') || null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [chatActive, setChatActive] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const endRef = useRef(null);

  const selectedUser = chatList.find((c) => c._id === selectedId);

  const loadChatList = useCallback(async () => {
    try {
      const { data } = await api.get('/direct-chat/admin/all');
      setChatList(data.chats || []);
    } catch {}
    setListLoading(false);
  }, []);

  const loadMessages = useCallback(async () => {
    if (!selectedId) return;
    try {
      const { data } = await api.get(`/direct-chat/${selectedId}/messages`);
      setMessages(data.messages || []);
    } catch {}
  }, [selectedId]);

  const loadStatus = useCallback(async () => {
    if (!selectedId) return;
    try {
      const { data } = await api.get(`/direct-chat/${selectedId}/status`);
      setChatActive(data.active);
    } catch {}
  }, [selectedId]);

  // Initial load
  useEffect(() => {
    loadChatList();
  }, [loadChatList]);

  // When selecting a chat
  useEffect(() => {
    if (!selectedId) return;
    loadMessages();
    loadStatus();

    // RTDB listener for real-time updates
    let unsub;
    try {
      const chatRef = ref(rtdb, `directChats/${selectedId}`);
      unsub = onValue(chatRef, () => {
        loadMessages();
        loadChatList();
      }, () => {});
    } catch {}

    // Polling fallback
    const poll = setInterval(() => {
      loadMessages();
      loadChatList();
    }, 5000);

    return () => {
      if (unsub) unsub();
      clearInterval(poll);
    };
  }, [selectedId, loadMessages, loadStatus, loadChatList]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const selectChat = (id) => {
    setSelectedId(id);
    setSearchParams({ user: id });
    setMobileShowChat(true);
    setMessages([]);
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/direct-chat/${selectedId}/message`, { text });
      setMessages((prev) => [...prev, data]);
      setInput('');
      loadChatList();
    } catch {}
    setSending(false);
  };

  const handleToggleChat = async () => {
    if (!selectedId) return;
    setActionLoading(true);
    try {
      if (chatActive) {
        await api.post(`/direct-chat/${selectedId}/close`);
        setChatActive(false);
      } else {
        await api.post(`/direct-chat/${selectedId}/open`);
        setChatActive(true);
      }
      loadChatList();
    } catch {}
    setActionLoading(false);
  };

  const handleBack = () => {
    setMobileShowChat(false);
  };

  return (
    <div style={s.page} className="page-content">
      {/* Header */}
      <div style={s.header}>
        <button onClick={() => navigate('/admin')} style={s.backLink}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          Admin
        </button>
        <h1 style={s.title}>Direct Chats</h1>
        <span style={s.badge}>{chatList.filter((c) => c.active).length} active</span>
      </div>

      <div style={s.container}>
        {/* Chat list sidebar */}
        <div style={{ ...s.sidebar, ...(mobileShowChat ? s.sidebarHideMobile : {}) }}>
          {listLoading && <p style={s.emptyText}>Loading...</p>}
          {!listLoading && chatList.length === 0 && (
            <div style={s.emptyState}>
              <p style={s.emptyIcon}>💬</p>
              <p style={s.emptyTitle}>No chats yet</p>
              <p style={s.emptyDesc}>Start a chat from any user's profile page</p>
            </div>
          )}
          {chatList.map((c) => (
            <button
              key={c._id}
              onClick={() => selectChat(c._id)}
              style={{ ...s.chatItem, ...(selectedId === c._id ? s.chatItemActive : {}) }}
            >
              <div style={s.chatItemLeft}>
                <div style={{ ...s.chatAvatar, background: c.active ? 'var(--navy)' : 'var(--border)' }}>
                  {(c.name?.[0] || c.phone?.slice(-2) || '?').toUpperCase()}
                </div>
                <div style={s.chatItemInfo}>
                  <div style={s.chatItemTop}>
                    <span style={s.chatItemName}>{c.name || c.phone}</span>
                    {c.active && <span style={s.activeDot} />}
                  </div>
                  {c.lastMessage && (
                    <p style={s.chatItemPreview}>
                      {c.lastMessage.sender === 'admin' ? 'You: ' : ''}{c.lastMessage.text.slice(0, 50)}{c.lastMessage.text.length > 50 ? '...' : ''}
                    </p>
                  )}
                </div>
              </div>
              {c.lastMessage && (
                <span style={s.chatItemTime}>
                  {formatTime(c.lastMessage.createdAt)}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Chat area */}
        <div style={{ ...s.chatArea, ...(mobileShowChat ? {} : s.chatAreaHideMobile) }}>
          {!selectedId ? (
            <div style={s.noChatSelected}>
              <p style={{ fontSize: '48px' }}>💬</p>
              <p style={s.noChatText}>Select a conversation</p>
            </div>
          ) : (
            <>
              {/* Chat header */}
              <div style={s.chatHeader}>
                <button onClick={handleBack} style={s.mobileBack}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
                <div style={s.chatHeaderInfo}>
                  <p style={s.chatHeaderName}>{selectedUser?.name || selectedUser?.phone || '...'}</p>
                  <p style={s.chatHeaderSub}>{selectedUser?.phone}</p>
                </div>
                <div style={s.chatHeaderActions}>
                  <button
                    onClick={() => navigate(`/admin/users/${selectedId}`)}
                    style={s.profileBtn}
                  >
                    Profile
                  </button>
                  <button
                    onClick={handleToggleChat}
                    disabled={actionLoading}
                    style={chatActive ? s.endBtn : s.startBtn}
                  >
                    {actionLoading ? '...' : chatActive ? 'End Chat' : 'Start Chat'}
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div style={s.messagesArea}>
                {messages.length === 0 && (
                  <p style={s.emptyMessages}>No messages yet</p>
                )}
                {messages.map((msg) => (
                  <div key={msg._id} style={{ ...s.msgRow, justifyContent: msg.sender === 'admin' ? 'flex-end' : 'flex-start' }}>
                    <div style={msg.sender === 'admin' ? s.msgAdmin : s.msgUser}>
                      <p style={s.msgText}>{msg.text}</p>
                      <span style={s.msgTime}>
                        {new Date(msg.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>

              {/* Input */}
              {chatActive && (
                <div style={s.inputArea}>
                  <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                    placeholder="Type a message..."
                    style={s.inputField}
                    autoFocus
                  />
                  <button
                    onClick={handleSend}
                    disabled={!input.trim() || sending}
                    style={{ ...s.sendBtn, opacity: input.trim() && !sending ? 1 : 0.4 }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                  </button>
                </div>
              )}
              {!chatActive && (
                <div style={s.closedBar}>
                  Chat is closed. Click "Start Chat" to reopen.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function formatTime(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  if (diff < 86400000 && d.getDate() === now.getDate()) {
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  }
  if (diff < 7 * 86400000) {
    return d.toLocaleDateString('en-IN', { weekday: 'short' });
  }
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

const s = {
  page: { maxWidth: '1200px', margin: '0 auto', padding: '20px 16px', height: 'calc(100vh - 70px)', display: 'flex', flexDirection: 'column' },
  header: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', flexShrink: 0 },
  backLink: { background: 'none', border: 'none', color: 'var(--navy)', fontWeight: '700', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', padding: 0, fontFamily: "'DM Sans', sans-serif" },
  title: { fontFamily: "'Playfair Display', Georgia, serif", fontSize: '20px', fontWeight: '700', color: 'var(--navy)', margin: 0 },
  badge: { fontSize: '11px', background: 'var(--success-bg)', color: 'var(--success)', fontWeight: '700', padding: '4px 12px', borderRadius: '99px' },

  container: { flex: 1, display: 'flex', gap: '0', background: '#fff', border: '1px solid var(--border)', borderRadius: '16px', overflow: 'hidden', minHeight: 0 },

  sidebar: { width: '320px', flexShrink: 0, borderRight: '1px solid var(--border)', overflowY: 'auto', background: '#fafbfc' },
  sidebarHideMobile: {},
  chatItem: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', width: '100%', padding: '14px 16px', border: 'none', borderBottom: '1px solid var(--border-2)', background: 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: "'DM Sans', sans-serif" },
  chatItemActive: { background: 'var(--gold-light)' },
  chatItemLeft: { display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 },
  chatAvatar: { width: '40px', height: '40px', borderRadius: '50%', color: '#fff', fontSize: '14px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: "'Playfair Display', Georgia, serif" },
  chatItemInfo: { flex: 1, minWidth: 0 },
  chatItemTop: { display: 'flex', alignItems: 'center', gap: '6px' },
  chatItemName: { fontSize: '13px', fontWeight: '700', color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  activeDot: { width: '7px', height: '7px', borderRadius: '50%', background: 'var(--success)', flexShrink: 0 },
  chatItemPreview: { fontSize: '12px', color: 'var(--text-3)', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  chatItemTime: { fontSize: '10px', color: 'var(--text-3)', whiteSpace: 'nowrap', flexShrink: 0 },

  chatArea: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
  chatAreaHideMobile: {},
  noChatSelected: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)' },
  noChatText: { fontSize: '15px', fontWeight: '600', color: 'var(--text-3)', marginTop: '8px' },

  chatHeader: { display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 20px', borderBottom: '1px solid var(--border-2)', flexShrink: 0 },
  mobileBack: { display: 'none', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--navy)', padding: 0 },
  chatHeaderInfo: { flex: 1, minWidth: 0 },
  chatHeaderName: { fontSize: '14px', fontWeight: '700', color: 'var(--navy)', margin: 0 },
  chatHeaderSub: { fontSize: '11px', color: 'var(--text-3)', margin: 0 },
  chatHeaderActions: { display: 'flex', gap: '8px', flexShrink: 0 },
  profileBtn: { background: 'var(--gold-light)', color: 'var(--navy)', border: '1px solid var(--gold-mid)', padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  startBtn: { background: 'var(--navy)', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  endBtn: { background: '#fff', color: 'var(--danger)', border: '1px solid rgba(185,28,28,0.3)', padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },

  messagesArea: { flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '8px', background: '#f7f8fa' },
  emptyMessages: { textAlign: 'center', color: 'var(--text-3)', fontSize: '13px', padding: '40px 20px' },
  msgRow: { display: 'flex' },
  msgAdmin: { background: 'var(--navy)', color: '#fff', borderRadius: '14px 14px 4px 14px', padding: '10px 14px', maxWidth: '70%' },
  msgUser: { background: '#fff', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '14px 14px 14px 4px', padding: '10px 14px', maxWidth: '70%' },
  msgText: { fontSize: '13px', lineHeight: '1.5', margin: 0, wordBreak: 'break-word' },
  msgTime: { fontSize: '10px', opacity: 0.5, marginTop: '4px', display: 'block', textAlign: 'right' },

  inputArea: { display: 'flex', gap: '10px', padding: '14px 20px', borderTop: '1px solid var(--border-2)', background: '#fff', flexShrink: 0 },
  inputField: { flex: 1, padding: '11px 16px', borderRadius: '12px', border: '1px solid var(--border)', fontSize: '14px', fontFamily: "'DM Sans', sans-serif", outline: 'none', background: 'var(--bg)' },
  sendBtn: { background: 'var(--navy)', color: '#fff', border: 'none', width: '44px', height: '44px', borderRadius: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  closedBar: { padding: '14px 20px', textAlign: 'center', fontSize: '13px', color: 'var(--text-3)', background: 'var(--bg)', borderTop: '1px solid var(--border-2)', flexShrink: 0 },

  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', textAlign: 'center' },
  emptyIcon: { fontSize: '40px', marginBottom: '8px' },
  emptyTitle: { fontSize: '15px', fontWeight: '700', color: 'var(--navy)', margin: 0 },
  emptyDesc: { fontSize: '13px', color: 'var(--text-3)', marginTop: '4px' },
  emptyText: { padding: '20px', textAlign: 'center', color: 'var(--text-3)', fontSize: '13px' },
};

// Mobile responsive — add a style tag for media queries
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @media (max-width: 700px) {
    .admin-chat-sidebar-hide { display: none !important; }
    .admin-chat-area-hide { display: none !important; }
    .admin-chat-mobile-back { display: flex !important; }
    .admin-chat-sidebar { width: 100% !important; border-right: none !important; }
    .admin-chat-area { width: 100% !important; }
  }
`;
if (!document.querySelector('[data-admin-chat-styles]')) {
  styleSheet.setAttribute('data-admin-chat-styles', '');
  document.head.appendChild(styleSheet);
}
